import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import { authApi } from '../../api/auth'
import { searchApi } from '../../api/search'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Loading from '../../components/common/Loading'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import { FiUser, FiMail, FiPhone, FiMapPin, FiBook, FiAward, FiCalendar } from 'react-icons/fi'
import './Settings.css'

const Settings = () => {
  const { user, isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [isEditingPreferences, setIsEditingPreferences] = useState(false)
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    university_id: user?.university_id || '',
    college_id: user?.college_id || '',
    department_id: user?.department_id || '',
    specialization_id: user?.specialization_id || '',
    semester_id: user?.semester_id || '',
    university_band_id: user?.university_band_id || '',
  })

  // Fetch universities, colleges, etc. for dropdowns
  const { data: universitiesData } = useQuery({
    queryKey: ['universities'],
    queryFn: () => authApi.getUniversities(),
    enabled: isEditingPreferences,
  })

  const { data: collegesData } = useQuery({
    queryKey: ['colleges', formData.university_id],
    queryFn: () => searchApi.getColleges(formData.university_id),
    enabled: isEditingPreferences && !!formData.university_id,
  })

  const { data: departmentsData, isLoading: isLoadingDepartments } = useQuery({
    queryKey: ['departments', formData.college_id, formData.university_band_id],
    queryFn: () => searchApi.getDepartments(formData.college_id, formData.university_band_id || null),
    enabled: isEditingPreferences && !!formData.college_id && !!formData.university_band_id,
  })

  const { data: specializationsData } = useQuery({
    queryKey: ['specializations', formData.department_id],
    queryFn: () => searchApi.getSpecializations(formData.department_id),
    enabled: isEditingPreferences && !!formData.department_id,
  })

  const { data: semestersData } = useQuery({
    queryKey: ['semesters', formData.university_id],
    queryFn: () => searchApi.getSemesters(formData.university_id),
    enabled: isEditingPreferences && !!formData.university_id,
  })

  const { data: universityBandsData } = useQuery({
    queryKey: ['university-bands', formData.college_id],
    queryFn: () => searchApi.getUniversityBands(formData.college_id),
    enabled: isEditingPreferences && !!formData.college_id,
  })

  const updateProfileMutation = useMutation({
    mutationFn: (data) => authApi.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['user'])
      setIsEditing(false)
      alert('تم تحديث الملف الشخصي بنجاح')
    },
    onError: (error) => {
      alert(error.response?.data?.message || 'حدث خطأ أثناء التحديث')
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    // Send only keys the backend expects; '' -> null so Laravel nullable|exists and DB accept
    const payload = {
      name: formData.name,
      phone: formData.phone || null,
      university_id: formData.university_id || null,
      college_id: formData.college_id || null,
      department_id: formData.department_id || null,
      specialization_id: formData.specialization_id || null,
      university_band_id: formData.university_band_id || null,
      semester_id: formData.semester_id || null,
    }
    updateProfileMutation.mutate(payload)
  }

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value }
      // Reset dependent fields when parent changes
      if (field === 'university_id') {
        newData.college_id = ''
        newData.department_id = ''
        newData.specialization_id = ''
        newData.semester_id = ''
        newData.university_band_id = ''
      } else if (field === 'college_id') {
        newData.university_band_id = ''
        newData.department_id = ''
        newData.specialization_id = ''
      } else if (field === 'university_band_id') {
        newData.department_id = ''
        newData.specialization_id = ''
      } else if (field === 'department_id') {
        newData.specialization_id = ''
      }
      return newData
    })
  }

  // Update formData when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        university_id: user?.university_id || '',
        college_id: user?.college_id || '',
        department_id: user?.department_id || '',
        specialization_id: user?.specialization_id || '',
        semester_id: user?.semester_id || '',
        university_band_id: user?.university_band_id || '',
      })
    }
  }, [user])

  if (!isAuthenticated) {
    return (
      <>
        <Header />
        <div className="error-container">
          <p>يجب تسجيل الدخول للوصول إلى الإعدادات</p>
        </div>
        <Footer />
      </>
    )
  }

  return (
    <div className="settings-page bg-gray-900 min-h-screen">
      <Header />
      <main className="settings-main bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          <h1 className="page-title text-white">الإعدادات والتفضيلات</h1>

          <div className="settings-content">
            {/* Personal Information */}
            <div className="settings-section">
              <div className="section-header">
                <h2 className="section-title">
                  <FiUser /> المعلومات الشخصية
                </h2>
                <Button
                  variant={isEditing ? 'outline' : 'primary'}
                  onClick={() => {
                    setIsEditing(!isEditing)
                    if (isEditing) {
                      setFormData({
                        name: user?.name || '',
                        email: user?.email || '',
                        phone: user?.phone || '',
                        university_id: user?.university_id || '',
                        college_id: user?.college_id || '',
                        department_id: user?.department_id || '',
                        specialization_id: user?.specialization_id || '',
                        semester_id: user?.semester_id || '',
                        university_band_id: user?.university_band_id || '',
                      })
                    }
                  }}
                >
                  {isEditing ? 'إلغاء' : 'تعديل'}
                </Button>
              </div>

              {isEditing ? (
                <form onSubmit={handleSubmit} className="settings-form">
                  <div className="form-row">
                    <Input
                      label="الاسم"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                    />
                    <Input
                      label="البريد الإلكتروني"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-row">
                    <Input
                      label="رقم الهاتف"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={updateProfileMutation.isLoading}
                    fullWidth
                  >
                    حفظ التغييرات
                  </Button>
                </form>
              ) : (
                <div className="info-grid">
                  <div className="info-item">
                    <FiUser className="info-icon" />
                    <div>
                      <span className="info-label">الاسم</span>
                      <span className="info-value">{user?.name || 'غير محدد'}</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FiMail className="info-icon" />
                    <div>
                      <span className="info-label">البريد الإلكتروني</span>
                      <span className="info-value">{user?.email || 'غير محدد'}</span>
                    </div>
                  </div>
                  {user?.phone && (
                    <div className="info-item">
                      <FiPhone className="info-icon" />
                      <div>
                        <span className="info-label">رقم الهاتف</span>
                        <span className="info-value">{user.phone}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Academic Preferences */}
            <div className="settings-section">
              <div className="section-header">
                <h2 className="section-title">
                  <FiAward /> التفضيلات الأكاديمية
                </h2>
                <Button
                  variant={isEditingPreferences ? 'outline' : 'primary'}
                  onClick={() => {
                    setIsEditingPreferences(!isEditingPreferences)
                    if (isEditingPreferences) {
                      setFormData(prev => ({
                        ...prev,
                        university_id: user?.university_id || '',
                        college_id: user?.college_id || '',
                        department_id: user?.department_id || '',
                        specialization_id: user?.specialization_id || '',
                        semester_id: user?.semester_id || '',
                        university_band_id: user?.university_band_id || '',
                      }))
                    }
                  }}
                >
                  {isEditingPreferences ? 'إلغاء' : 'تعديل'}
                </Button>
              </div>

              {isEditingPreferences ? (
                <form onSubmit={handleSubmit} className="settings-form">
                  <div className="form-group">
                    <label htmlFor="university_id">الجامعة *</label>
                    <select
                      id="university_id"
                      value={formData.university_id}
                      onChange={(e) => handleChange('university_id', e.target.value)}
                      required
                      className="form-select"
                    >
                      <option value="">اختر الجامعة</option>
                      {(universitiesData?.data?.universities || universitiesData?.data || []).map((uni) => (
                        <option key={uni.id} value={uni.id}>
                          {uni.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.university_id && (collegesData?.data?.colleges || collegesData?.data?.faculties || collegesData?.data) && (
                    <div className="form-group">
                      <label htmlFor="college_id">الكلية</label>
                      <select
                        id="college_id"
                        value={formData.college_id}
                        onChange={(e) => handleChange('college_id', e.target.value)}
                        className="form-select"
                      >
                        <option value="">اختر الكلية</option>
                        {(collegesData?.data?.colleges || collegesData?.data?.faculties || collegesData?.data || []).map((college) => (
                          <option key={college.id} value={college.id}>
                            {college.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* University Bands: only bands that have departments for this college; shown when college is selected */}
                  {formData.college_id && (universityBandsData?.data?.university_bands || universityBandsData?.data) && (
                    <div className="form-group">
                      <label htmlFor="university_band_id">الفرقة الدراسية</label>
                      <select
                        id="university_band_id"
                        value={formData.university_band_id}
                        onChange={(e) => handleChange('university_band_id', e.target.value)}
                        className="form-select"
                      >
                        <option value="">اختر الفرقة</option>
                        {(universityBandsData?.data?.university_bands || universityBandsData?.data || []).map((band) => (
                          <option key={band.id} value={band.id}>
                            {band.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Departments: only when a band is selected AND that band has departments; hide if band has no departments */}
                  {formData.college_id && formData.university_band_id && (isLoadingDepartments || ((departmentsData?.data?.departments || departmentsData?.data || []).length > 0)) && (
                    <div className="form-group">
                      <label htmlFor="department_id">القسم</label>
                      <select
                        id="department_id"
                        value={formData.department_id}
                        onChange={(e) => handleChange('department_id', e.target.value)}
                        className="form-select"
                      >
                        <option value="">اختر القسم</option>
                        {(departmentsData?.data?.departments || departmentsData?.data || []).map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Specializations: only when department is selected AND the band has departments */}
                  {formData.department_id && formData.university_band_id && (isLoadingDepartments || ((departmentsData?.data?.departments || departmentsData?.data || []).length > 0)) && (
                    <div className="form-group">
                      <label htmlFor="specialization_id">التخصص</label>
                      <select
                        id="specialization_id"
                        value={formData.specialization_id}
                        onChange={(e) => handleChange('specialization_id', e.target.value)}
                        className="form-select"
                      >
                        <option value="">اختر التخصص</option>
                        {(specializationsData?.data?.specializations || specializationsData?.data || []).map((spec) => (
                          <option key={spec.id} value={spec.id}>
                            {spec.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {formData.university_id && (semestersData?.data?.semesters || semestersData?.data) && (
                    <div className="form-group">
                      <label htmlFor="semester_id">الفصل الدراسي</label>
                      <select
                        id="semester_id"
                        value={formData.semester_id}
                        onChange={(e) => handleChange('semester_id', e.target.value)}
                        className="form-select"
                      >
                        <option value="">اختر الفصل الدراسي</option>
                        {(semestersData?.data?.semesters || semestersData?.data || []).map((semester) => (
                          <option key={semester.id} value={semester.id}>
                            {semester.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    loading={updateProfileMutation.isLoading}
                    fullWidth
                  >
                    حفظ التفضيلات
                  </Button>
                </form>
              ) : (
                <div className="info-grid">
                {user?.university && (
                  <div className="info-item">
                    <FiMapPin className="info-icon" />
                    <div>
                      <span className="info-label">الجامعة</span>
                      <span className="info-value">{user.university.name || user.university}</span>
                    </div>
                  </div>
                )}
                {user?.college && (
                  <div className="info-item">
                    <FiBook className="info-icon" />
                    <div>
                      <span className="info-label">الكلية</span>
                      <span className="info-value">{user.college.name || user.college}</span>
                    </div>
                  </div>
                )}
                {user?.universityBand && (
                  <div className="info-item">
                    <FiBook className="info-icon" />
                    <div>
                      <span className="info-label">الفرقة الدراسية</span>
                      <span className="info-value">{user.universityBand.name || user.universityBand}</span>
                    </div>
                  </div>
                )}
                {user?.department && (
                  <div className="info-item">
                    <FiBook className="info-icon" />
                    <div>
                      <span className="info-label">القسم</span>
                      <span className="info-value">{user.department.name || user.department}</span>
                    </div>
                  </div>
                )}
                {user?.specialization && (
                  <div className="info-item">
                    <FiBook className="info-icon" />
                    <div>
                      <span className="info-label">التخصص</span>
                      <span className="info-value">{user.specialization.name || user.specialization}</span>
                    </div>
                  </div>
                )}
                {user?.semester && (
                  <div className="info-item">
                    <FiCalendar className="info-icon" />
                    <div>
                      <span className="info-label">الفصل الدراسي</span>
                      <span className="info-value">{user.semester.name || user.semester}</span>
                    </div>
                  </div>
                )}
                {(!user?.university && !user?.college) && (
                  <div className="empty-preferences">
                    <p>لم يتم تحديد التفضيلات الأكاديمية</p>
                    <p className="empty-subtitle">يمكنك تحديثها من صفحة الملف الشخصي</p>
                  </div>
                )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Settings

